import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";

export interface BetStat {
  /** What the figure is. */
  label: string;
  /** The figure itself. */
  value: ReactNode;
  /** `win` colors money the player stands to make; `lose` what it costs. */
  tone?: "win" | "lose";
}

const StatList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius,
  border: "1px solid",
  borderColor: theme.palette.divider,
  overflow: "hidden",

  // The panel is white in the light scheme and the divider is a 10% warm grey,
  // so a bare outline leaves the figures floating. A tinted face is what makes
  // them read as one readout.
  backgroundColor: theme.vars.palette.game.felt,
  ...theme.applyStyles("dark", { backgroundColor: "transparent" }),
}));

const StatRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.75, 1.25),

  "& + &": {
    borderTop: "1px solid",
    borderColor: theme.palette.divider,
  },
}));

/**
 * The figure.
 *
 * A span rather than Typography's paragraph, because a figure is sometimes a
 * field the player types into - dice aims its bar from the multiplier - and a
 * `<div>` inside a `<p>` is not markup a browser will keep nested.
 */
const StatValue = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone?: BetStat["tone"] }>(({ theme, tone }) => ({
  ...theme.typography.body2,
  display: "block",
  fontWeight: 700,
  textAlign: "right",
  // The figures move as the player types; without this they jitter.
  fontVariantNumeric: "tabular-nums",
  // The outcome tokens rather than `success.main`/`error.main`: the light
  // scheme's success is the brand's neon `#36FF00`, which is a 1.4:1 figure on
  // a white panel. `palette.game` carries a win color chosen per scheme to be
  // legible as text, which is all this ever uses it as.
  color:
    tone === "win"
      ? theme.vars.palette.game.win
      : tone === "lose"
        ? theme.vars.palette.game.lose
        : theme.vars.palette.text.primary,
}));

/**
 * The figures under a game's controls: the edge, the win chance, what a win
 * pays at the stake currently typed.
 *
 * One per line, label against number. Three of them across a 320px column -
 * which is what every game did when the controls had the page width - wraps
 * each heading onto two lines and leaves the player reading fragments.
 */
const BetStats = ({ stats }: { stats: BetStat[] }) => (
  <StatList>
    {stats.map(({ label, value, tone }) => (
      <StatRow key={label}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <StatValue tone={tone}>{value}</StatValue>
      </StatRow>
    ))}
  </StatList>
);

export default BetStats;
